import { Storage } from './storage.js';
import { qs, qsa, getTotalRounds } from './utils.js';

/** @typedef {{recoverable: boolean, code: 'read-failed'|'corrupt-json'|'invalid-state'|'storage-unavailable'|'quota-exceeded'|'write-failed'|'remove-failed', operation: 'probe'|'read'|'write'|'remove', key: string|null, cause: unknown|null}} StorageIssue */
/** @typedef {{ok: boolean, changed: boolean, persisted: boolean, value: unknown, issue: StorageIssue|null}} PersistenceOutcome */

const IMPORT_MESSAGES = {
  'no-file': 'Choose a backup file first.',
  'read-failed': 'That backup could not be read.',
  'invalid-json': 'That file is not valid JSON.',
  'invalid-shape': 'That backup does not contain valid progress.',
  'unsupported-version': 'That backup is from a newer version of the app.',
  'persist-failed': 'That backup could not be saved.',
};

export default class ProgressActions {
  /** Creates delegated persistence controls and a serialized mutation queue. @param {{store: import('./progress-store.js').default, ui: import('./progress-ui.js').default, feedback: import('./feedback-ui.js').default, root?: Document|Element}} options */
  constructor({ store, ui, feedback, root = document }) {
    this.store = store;
    this.ui = ui;
    this.feedback = feedback;
    this.root = root;
    this.storage = Storage;
    this.mutationQueue = Promise.resolve();
    this.isWired = false;
    this.onClick = (event) => {
      const control = event.target.closest?.('[data-progress-action]');
      if (!control || !this.root.contains(control)) return;
      void this.handleAction(control).catch((error) => console.warn('[ProgressActions]', error));
    };
    this.onChange = (event) => {
      const input = event.target.closest?.('[data-progress-import]');
      if (!input || !this.root.contains(input)) return;
      void this.importProgress(input.files?.[0]);
      input.value = '';
    };
  }

  /** Wires delegated click and import-change handlers once. @returns {void} */
  wire() {
    if (this.isWired) return;
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('change', this.onChange);
    this.isWired = true;
  }

  /** Removes delegated action handlers. @returns {void} */
  destroy() {
    if (!this.isWired) return;
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('change', this.onChange);
    this.isWired = false;
  }

  /** @private Routes one delegated action control to its command. @param {Element} control @returns {Promise<unknown>} */
  async handleAction(control) {
    const { progressAction: action, week, day, blockId } = control.dataset;
    if (action === 'export') return this.exportProgress();
    if (action === 'reset-block') return this.resetBlock(week, day, blockId, control);
    if (action === 'reset-day') return this.resetDay(week, day, control);
    if (action === 'reset-all') return this.resetAll(control);
    if (action === 'retry-storage') return this.retryStorage();
    if (action === 'download-damaged') return this.downloadDamaged();
    if (action === 'discard-damaged') return this.discardDamaged(control);
    return undefined;
  }

  /** Downloads the current backup and reports export failures through feedback UI. @returns {void} */
  exportProgress() {
    try {
      this.store.export();
      this.feedback.showToast('Progress exported.');
    } catch (error) {
      this.showPersistenceIssue(error?.issue);
    }
  }

  /** @private Serializes mutations, swallowing a prior rejection only to keep later actions runnable. @param {() => Promise<unknown>|unknown} operation @returns {Promise<unknown>} */
  queueMutation(operation) {
    const next = this.mutationQueue.catch(() => undefined).then(operation);
    this.mutationQueue = next.catch(() => undefined);
    return next;
  }

  /** Queues an import and its read-only UI rendering. @param {File} file @returns {Promise<void>} */
  importProgress(file) {
    return this.queueMutation(() => this._importProgress(file));
  }

  /** @private Imports once, preserving in-tab rendering and feedback for recoverable save failure. */
  async _importProgress(file) {
    try {
      await this.store.import(file);
      this.renderImportedPage();
      this.feedback.showToast('Backup imported.');
    } catch (error) {
      if (error?.code === 'persist-failed') {
        this.renderImportedPage();
        this.showPersistenceIssue(error.outcome?.issue);
        this.feedback.showToast('Imported changes are only saved in this tab.', { tone: 'alert' });
        return;
      }
      this.feedback.showToast(
        IMPORT_MESSAGES[error?.code] ?? 'That backup could not be imported.',
        {
          tone: 'alert',
        }
      );
    }
  }

  /** Queues a confirmed block reset. @param {number|string} week @param {number|string} day @param {string} blockId @param {Element|null} [invoker] @returns {Promise<void>} */
  resetBlock(week, day, blockId, invoker) {
    return this.queueMutation(() => this._resetBlock(week, day, blockId, invoker));
  }

  /** @private Confirms, resets, repaints, then reports persistence outcome. */
  async _resetBlock(week, day, blockId, invoker) {
    const confirmed = await this.feedback.confirm({
      title: 'Reset this block?',
      message:
        'This removes the saved exercise and round progress for this block. This can’t be undone.',
      confirmLabel: 'Reset block',
      invoker,
    });
    if (!confirmed) return;
    const block = qs(`[data-block-id="${blockId}"]`, this.root);
    const result = this.store.resetBlock(week, day, blockId, block);
    if (block) this.repaintBlock(week, day, block);
    this.ui.updateDayProgressBars(week, day);
    if (!this.handlePersistenceOutcome(result)) return;
    this.feedback.showToast('Block progress reset.');
  }

  /** Queues a confirmed day reset. @param {number|string} week @param {number|string} day @param {Element|null} [invoker] @returns {Promise<void>} */
  resetDay(week, day, invoker) {
    return this.queueMutation(() => this._resetDay(week, day, invoker));
  }

  /** @private Confirms and repaints a day reset before user feedback. */
  async _resetDay(week, day, invoker) {
    const confirmed = await this.feedback.confirm({
      title: `Reset Day ${day} progress?`,
      message: `This removes the saved exercise and round progress for Day ${day}. This can’t be undone.`,
      confirmLabel: 'Reset day',
      invoker,
    });
    if (!confirmed) return;
    const result = this.store.resetDay(week, day);
    this.repaintDay(week, day);
    if (!this.handlePersistenceOutcome(result)) return;
    this.feedback.showToast('Day progress reset.');
  }

  /** Queues a confirmed programme reset. @param {Element|null} [invoker] @returns {Promise<void>} */
  resetAll(invoker) {
    return this.queueMutation(() => this._resetAll(invoker));
  }

  /** @private Confirms a complete reset and refreshes every configured day bar. */
  async _resetAll(invoker) {
    const confirmed = await this.feedback.confirm({
      title: 'Reset all progress?',
      message:
        'This removes every completed exercise and round across the eight-week programme. This can’t be undone.',
      confirmLabel: 'Reset all progress',
      invoker,
    });
    if (!confirmed) return;
    const result = this.store.resetAll();
    qsa('.progress-bar[data-week][data-day]', this.root).forEach((bar) => {
      this.ui.updateDayProgressBars(bar.dataset.week, bar.dataset.day);
    });
    this.repaintCurrentPage();
    if (!this.handlePersistenceOutcome(result)) return;
    this.feedback.showToast('All progress reset.');
  }

  /** Queues a storage retry followed by a save of dirty state. @returns {Promise<boolean>} */
  retryStorage() {
    return this.queueMutation(() => this._retryStorage());
  }

  /** @private Replays dirty persistence after a successful availability probe. */
  _retryStorage() {
    if (!this.storage.retry({ notify: false })) return false;
    const result = this.store.save();
    if (result.persisted) {
      this.feedback.clearIssue();
      this.feedback.showToast('Saving restored');
      return true;
    }
    this.feedback.removeToast?.();
    this.showPersistenceIssue(result.issue);
    return false;
  }

  /** Downloads preserved raw corrupt entries as a recovery backup. @returns {void} */
  downloadDamaged() {
    let url;
    let anchor;
    try {
      const blob = new Blob([JSON.stringify(this.store.getCorruptBackup(), null, 2)], {
        type: 'application/json',
      });
      url = URL.createObjectURL(blob);
      anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'damaged-workout-progress.json';
      document.body.appendChild(anchor);
      anchor.click();
    } catch {
      this.feedback.showToast('Damaged data could not be downloaded.', { tone: 'alert' });
    } finally {
      anchor?.remove();
      if (url) URL.revokeObjectURL(url);
    }
  }

  /** Queues a confirmed discard of damaged progress maps. @param {Element|null} [invoker] @returns {Promise<void>} */
  discardDamaged(invoker) {
    return this.queueMutation(() => this._discardDamaged(invoker));
  }

  /** @private Confirms discard, refreshes UI, and reports any remaining corrupt data. */
  async _discardDamaged(invoker) {
    const confirmed = await this.feedback.confirm({
      title: 'Reset saved progress?',
      message: 'This removes the unreadable saved data. This can’t be undone.',
      confirmLabel: 'Reset saved progress',
      invoker,
    });
    if (!confirmed) return;
    const result = this.store.discardCorrupt();
    this.repaintCurrentPage();
    if (!result.persisted) {
      const remaining = this.store.getCorruptBackup().entries;
      if (Object.keys(remaining).length > 0) {
        this.feedback.showIssue({ code: 'invalid-state', operation: 'read', key: null });
        this.feedback.showToast('Some damaged progress could not be reset.', { tone: 'alert' });
      } else this.showPersistenceIssue(result.issue);
      return;
    }
    this.feedback.clearIssue();
    this.feedback.showToast('Saved progress reset.');
  }

  /** @private Reports a non-persisted mutation and returns whether it safely persisted. @param {PersistenceOutcome} result @returns {boolean} */
  handlePersistenceOutcome(result) {
    if (result?.persisted !== false) return true;
    this.showPersistenceIssue(result.issue);
    return false;
  }

  /** @private Shows a supplied issue or the standard write failure. @param {StorageIssue|null|undefined} issue @returns {void} */
  showPersistenceIssue(issue) {
    this.feedback.showIssue(
      issue ?? { code: 'write-failed', operation: 'write', key: null, cause: null }
    );
  }

  /** @private Returns the current day page metadata when present. @returns {{week: string, day: string}|null} */
  currentContext() {
    const meta = qs('#page-meta[data-week][data-day]', this.root);
    return meta ? { week: meta.dataset.week, day: meta.dataset.day } : null;
  }

  /** @private Reconciles the active day or all configured summary bars after a mutation. @returns {void} */
  repaintCurrentPage() {
    const context = this.currentContext();
    if (context) {
      this.repaintDay(context.week, context.day);
      return;
    }
    qsa('.progress-bar[data-week][data-day]', this.root).forEach((bar) => {
      this.ui.updateDayProgressBars(bar.dataset.week, bar.dataset.day);
    });
  }

  /** @private Renders imported stored state without writing compatibility state back to storage. @returns {void} */
  renderImportedPage() {
    const context = this.currentContext();
    if (context) {
      qsa('.exercise-block[data-block-id]', this.root).forEach((block) => {
        this.ui.renderBlockStoredState(context.week, context.day, block);
      });
      this.ui.updateDayProgressBars(context.week, context.day);
      return;
    }
    qsa('.progress-bar[data-week][data-day]', this.root).forEach((bar) => {
      this.ui.updateDayProgressBars(bar.dataset.week, bar.dataset.day);
    });
  }

  /** @private Reconciles every rendered block and progress bar for one day. @param {number|string} week @param {number|string} day @returns {void} */
  repaintDay(week, day) {
    qsa('.exercise-block[data-block-id]', this.root).forEach((block) =>
      this.repaintBlock(week, day, block)
    );
    this.ui.updateDayProgressBars(week, day);
  }

  /** @private Reconciles one block's counter and current-round exercise state. @param {number|string} week @param {number|string} day @param {Element} block @returns {void} */
  repaintBlock(week, day, block) {
    const totalRounds = getTotalRounds(block);
    const blockId = block.getAttribute('data-block-id');
    const current = this.store.getCurrentRound(week, day, blockId, totalRounds);
    this.ui.updateBlockRoundsUI(block, current, totalRounds);
    this.ui.repaintBlockCurrentRound(week, day, block);
  }
}
